import {
  EMPTY_ADDRESS_VALUES,
  toAddressInput,
  type AddressFormat,
  type AddressFormValues,
} from "@repo/components/src/forms/address/helpers";
import type { TaxProfileAddressInputType } from "@repo/graphql/gql/graphql";

/** The part of a saved address the form prefills from. */
export type SavedAddress = {
  route: string;
  streetNumber: string;
  apartment: string;
  city: string;
  area: { id: string; region: { id: string } | null } | null;
};

/**
 * Prefills the address form from the saved address. An area below the top
 * level is picked as region + area; a top-level one is the region itself.
 */
export const addressFormValuesFromSaved = (
  saved: SavedAddress | null | undefined,
): AddressFormValues => {
  if (!saved) return EMPTY_ADDRESS_VALUES;
  const area = saved.area;
  const regionId = area ? (area.region?.id ?? area.id) : "";
  return {
    regionId,
    areaId: area && area.id !== regionId ? area.id : "",
    city: saved.city,
    route: saved.route,
    streetNumber: saved.streetNumber,
    apartment: saved.apartment,
  };
};

export const isAddressBlank = (values: AddressFormValues): boolean =>
  Object.values(values).every((value) => value.trim() === "");

/**
 * The address to send. An optional address left blank is omitted; a
 * required one is validated before this runs.
 */
export const buildAddressInput = (
  values: AddressFormValues,
  format: AddressFormat | null,
  required: boolean,
): TaxProfileAddressInputType | undefined =>
  !required && isAddressBlank(values)
    ? undefined
    : toAddressInput(values, format);

/** The address as one line for the review step; the area is named by the caller. */
export const formatAddressSummary = (
  input: TaxProfileAddressInputType,
  areaName: string | null,
): string => {
  const street = [input.route, input.streetNumber]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
  return [street, input.apartment, input.city, areaName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(", ");
};
